import { useContext, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AuthContext } from "react-oauth2-code-pkce";

import { useApp } from "../../app/Application.provider";
import {
  getActiveTenantCookie,
  getCurrentTenant,
  storeAuthState,
  validateRedirect,
} from "../../auth/utils";
import { SAFE_FALLBACK_ROUTE } from "../../auth/constants";
import { SessionConflictError } from "../../components/auth/SessionConflictError";

export const Route = createFileRoute("/auth/login")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const { redirect } = Route.useSearch();
  const { auth } = useApp();
  const { logIn, loginInProgress } = useContext(AuthContext);
  const navigate = useNavigate();

  // Single-client session enforcement: check if there's an active session on
  // a different tenant BEFORE initiating OAuth. This prevents the OAuth flow
  // from overwriting the active tenant cookie.
  const sessionConflict = useMemo(() => {
    const activeTenant = getActiveTenantCookie();
    const currentTenant = getCurrentTenant();

    if (activeTenant && currentTenant && activeTenant !== currentTenant) {
      return activeTenant;
    }

    return null;
  }, []);

  useEffect(() => {
    // If session conflict detected, don't proceed with login
    if (sessionConflict) return;

    // If already authenticated, skip login and go to the destination directly.
    if (auth.isAuthenticated) {
      void navigate({
        to: (redirect ? (validateRedirect(redirect) ?? SAFE_FALLBACK_ROUTE) : SAFE_FALLBACK_ROUTE) as string,
        replace: true,
      });

      return;
    }

    if (loginInProgress) return;

    // Clear any stale ROCP_* state from previous sessions or logout before initiating
    // a fresh OAuth flow. This prevents "Bad authorization state" errors when HQ/backend
    // redirects with OAuth callbacks that reference old PKCE state (LOA-1189 multi-tab issue).
    const ropKeys = Object.keys(localStorage).filter((k) => k.startsWith("ROCP_"));

    ropKeys.forEach((k) => localStorage.removeItem(k));

    // Validate the redirect target before encoding it into OAuth state.
    // Rejects external URLs, protocol-relative paths, and malformed strings.
    const safeRedirect = redirect ? (validateRedirect(redirect) ?? null) : null;

    // Encode the intended destination and current tenant into OAuth state.
    // preLogin() in oauth.config.ts reads this and passes it to the IdP as the state param.
    // At callback, consumeAuthState() recovers the destination and tenant.
    storeAuthState({
      redirect: safeRedirect,
      tenant: getCurrentTenant(),
    });

    logIn();
  }, [auth.isAuthenticated, loginInProgress, redirect, navigate, logIn, sessionConflict]);

  // Show session conflict error if user has active session on different tenant
  if (sessionConflict) {
    return <SessionConflictError />;
  }

  return (
    <div style={{ textAlign: "center", padding: "2rem" }}>
      <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>Redirecting to login…</p>
    </div>
  );
}
